import { P2GIcon, P2GText, SvgConfirmLarge } from '@parcel2go/core-ui';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import { IconBulletItem } from '../icon-bullet-item/icon-bullet-item';
import { Currency, DateElement, DateType, QuoteDates } from '~/lib/types/basket-types';
import { useMemo } from 'react';
import useBasketSection from '~/hooks/use-basket-section';
import { BasketSections } from '~/lib/types/enums';
import dayjs from 'dayjs';
import { getBasketCollectionDate } from '~/lib/basket';
import { toDisplayCurrency } from '~/lib/prices';
import { ContentBlock } from '../../content-block/content-block';

export type DefaultServiceBenefitsProps = {
  title: string;
  includedCoverAmountMinorCurrency: number;
  isDropOff: boolean;
  isLocker: boolean;
  requiresPrinter: boolean;
  currency: Currency;
  quoteDates: DateElement[];
};

export type BenefitItem = {
  benefitName: string;
  id: string;
};

export type BenefitProps = {
  content: React.ReactNode;
};

export const Benefit = ({ content }: BenefitProps): JSX.Element => {
  return (
    <IconBulletItem
      content={<P2GText>{content}</P2GText>}
      icon={
        <P2GIcon palette="success">
          <SvgConfirmLarge />
        </P2GIcon>
      }
    />
  );
};

export const DefaultServiceBenefits = ({
  title,
  includedCoverAmountMinorCurrency: includedCoverAmount,
  currency,
  isDropOff,
  isLocker,
  requiresPrinter,
  quoteDates
}: DefaultServiceBenefitsProps): JSX.Element => {
  const { t } = useTranslation([TranslationNamespaces.QUOTE]);
  const section = useBasketSection<QuoteDates>({ name: BasketSections.DATES });

  const collection = useMemo(() => {
    const collectionDate = getBasketCollectionDate(section.data) || '';

    return (
      quoteDates?.find(
        (date) => date.type === DateType.Collection && dayjs(date.collection?.date).isSame(collectionDate)
      ) ?? quoteDates?.[0]
    );
  }, [quoteDates, section?.data]);

  const deliveryDateMin = collection?.delivery?.dateMin ? dayjs(collection?.delivery?.dateMin).format('D MMM') : '';
  const deliveryDateMax = collection?.delivery?.dateMax ? dayjs(collection?.delivery?.dateMax).format('D MMM') : '';

  return (
    <ContentBlock title={title}>
      <Benefit
        content={
          isDropOff
            ? isLocker
              ? t('BENEFIT_DROP_AT_LOCKER_SERVICE', { defaultValue: 'Locker service' })
              : t('BENEFIT_DROP_AT_SHOP_SERVICE', { defaultValue: 'Drop at shop service' })
            : t('BENEFIT_COLLECTION_SERVICE', { defaultValue: 'Collection service' })
        }
      />

      {deliveryDateMin && (
        <Benefit
          content={t('BENEFIT_ESTIMATED_DELIVERY', {
            defaultValue: 'Estimated delivery\n{{deliveryRange}}',
            deliveryRange:
              deliveryDateMin !== deliveryDateMax && deliveryDateMax !== ''
                ? `${deliveryDateMin} - ${deliveryDateMax}`
                : `${deliveryDateMin}`
          })}
        />
      )}
      {includedCoverAmount > 0 && (
        <Benefit
          content={t('BENEFIT_INCLUDED_COVER_AMOUNT', {
            includedCoverAmount: toDisplayCurrency(currency, includedCoverAmount, 0),
            defaultValue: `{{includedCoverAmount}} inclusive Parcel Protection`
          })}
        />
      )}
      <Benefit content={t('BENEFIT_FREE_TRACKING', { defaultValue: 'Free parcel tracking' })} />
      <Benefit
        content={
          requiresPrinter
            ? t('BENEFIT_PRINTER_REQUIRED', { defaultValue: 'Printer required' })
            : t('BENEFIT_PRINTER_NOT_REQUIRED', { defaultValue: 'Printer not required' })
        }
      />
    </ContentBlock>
  );
};
